<template>
    <div class="ui-page">
        <ui-page-header title="Certidões de crédito (CHC)" subtitle="Certidões de Habilitação de Crédito enviadas pelo site depois do prazo de habilitação e divergência. Aprovada, os créditos entram no QGC vigente do caso.">
            <v-btn outlined color="primary" class="mr-2" :loading="dataList.loading" @click="load">
                <v-icon left small>mdi-refresh</v-icon>
                Atualizar
            </v-btn>
            <v-btn outlined color="primary" class="mr-2" :loading="exporting" @click="exportCsv">
                <v-icon left small>mdi-file-delimited-outline</v-icon>
                Exportar CSV
            </v-btn>
            <v-btn color="primary" depressed :loading="openingNext" @click="openNext">
                <v-icon left small>mdi-play-circle-outline</v-icon>
                Analisar próxima pendente
            </v-btn>
        </ui-page-header>

        <!-- Contadores por status: cada um filtra a fila ao clicar -->
        <div class="chc-cards">
            <button v-for="card in cards" :key="card.id" type="button" class="chc-card" :class="{ 'chc-card--active': dataList.filter.status === card.id }" @click="applyCard(card)">
                <span class="chc-card__label">{{ card.name }}</span>
                <span class="chc-card__value" :class="{ [`chc-card__value--${card.tone}`]: card.value > 0 }">{{ card.value }}</span>
            </button>
        </div>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="dataList.filter.term" placeholder="Buscar por protocolo, requerente, processo, credor ou CPF/CNPJ" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details clearable class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-autocomplete v-model="dataList.filter.client_id" :items="clients" item-value="id" item-text="name" placeholder="Todos os casos"
                    dense outlined hide-details clearable class="ui-toolbar__field" :menu-props="{ offsetY: true }" no-data-text="Nenhum caso encontrado" @change="applyFilters"></v-autocomplete>
                <v-select v-model="dataList.filter.requester_type" :items="requesterTypes" item-value="id" item-text="name" placeholder="Todos os requerentes"
                    dense outlined hide-details clearable class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-select v-model="dataList.filter.justice_branch" :items="justiceBranches" item-value="id" item-text="name" placeholder="Todos os ramos"
                    dense outlined hide-details clearable class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
            </template>

            <v-data-table :headers="headers" :items="dataList.data" :server-items-length="dataList.count" :options.sync="dataList.options"
                :loading="dataList.loading" disable-sort hide-default-footer no-data-text="Nenhuma CHC encontrada para os filtros"
                loading-text="Carregando dados..." class="ui-clickable-rows" @click:row="openItem">

                <template v-slot:[`item.protocol`]="{ item }">
                    <div class="ui-cell-main">{{ item.protocol }}</div>
                    <div class="ui-cell-sub">{{ formatDateTime(item.created_at) }}</div>
                </template>

                <template v-slot:[`item.requester_name`]="{ item }">
                    <div>{{ item.requester_name || '—' }}</div>
                    <div class="ui-cell-sub">{{ nameOf(requesterTypes, item.requester_type) }}</div>
                </template>

                <template v-slot:[`item.process_number`]="{ item }">
                    <div class="chc-nowrap">{{ item.process_number }}</div>
                    <div class="ui-cell-sub">{{ item.justice_branch ? nameOf(justiceBranches, item.justice_branch) : '—' }}</div>
                </template>

                <template v-slot:[`item.total_brl`]="{ item }">
                    {{ formatMoney(item.total_brl) }}
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="nameOf(statuses, item.status)" :tone="toneOf(statuses, item.status)" />
                </template>

                <template v-slot:[`item.actions`]>
                    <v-icon small color="grey">mdi-chevron-right</v-icon>
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.size"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>
    </div>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import { displayDate } from '@/common/util/date-time';
import { statuses, justiceBranches, requesterTypes, nameOf, toneOf } from "@/common/data/credit-certificate.data";

const emptyFilter = () => ({ term: "", client_id: null, requester_type: null, justice_branch: null, status: null });

// Fila de CHCs (Certidao de Habilitacao de Credito) da AJ.
export default {
    name: "CreditCertificateList",

    data: () => ({
        statuses,
        justiceBranches,
        requesterTypes,
        clients: [],
        counts: {},
        exporting: false,
        openingNext: false,
        headers: [
            { text: "Protocolo", value: "protocol", width: 150 },
            { text: "Caso", value: "client_name", width: 170 },
            { text: "Requerente", value: "requester_name", width: 170 },
            { text: "Processo de origem", value: "process_number", width: 200 },
            { text: "Credores", value: "creditors_count", width: 80, align: "center" },
            { text: "Total (R$)", value: "total_brl", width: 120, align: "end" },
            { text: "Status", value: "status", width: 170 },
            { text: "", value: "actions", width: 40, align: "end" },
        ],
        dataList: {
            filter: emptyFilter(),
            data: [],
            count: 0,
            loading: false,
            options: { page: 1, size: 25 },
        },
    }),

    computed: {
        hasFilters() {
            const f = this.dataList.filter;
            return !!(f.term || f.client_id || f.requester_type || f.justice_branch || f.status);
        },
        cards() {
            return statuses.map((status) => ({ ...status, value: this.counts[status.id] || 0 }));
        },
    },

    created() {
        this.loadClients();
        this.load();
    },

    methods: {
        nameOf,
        toneOf,

        formatDateTime(value) {
            // Data e hora gravadas pela API (UTC): exibidas no horario de Brasilia.
            return value ? displayDate(value).format('DD/MM/YYYY HH:mm') : '';
        },

        formatMoney(value) {
            return Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 500),

        applyFilters() {
            this.dataList.options.page = 1;
            this.load();
        },

        clearFilters() {
            this.dataList.filter = emptyFilter();
            this.applyFilters();
        },

        applyCard(card) {
            this.dataList.filter.status = this.dataList.filter.status === card.id ? null : card.id;
            this.applyFilters();
        },

        setPageSize(size) {
            this.dataList.options.size = size;
            this.applyFilters();
        },

        pageChanged(page) {
            this.dataList.options.page = page;
            this.load();
        },

        searchParams() {
            const search = {};
            Object.entries(this.dataList.filter).forEach(([key, value]) => {
                if (value !== null && value !== "") search[key] = value;
            });
            return JSON.stringify(search);
        },

        async load() {
            this.dataList.loading = true;
            try {
                const result = await axios.get('/api/admin/user-events/credit-certificates', {
                    params: { page: this.dataList.options.page, limit: this.dataList.options.size, search: this.searchParams() }
                });
                this.dataList.data = result.data.content.data;
                this.dataList.count = result.data.content.paging.total;
                this.counts = result.data.content.counts || {};
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        async loadClients() {
            try {
                const result = await axios.get('/api/clients', { params: { limit: 100000 } });
                this.clients = (result.data.content && result.data.content.data) || [];
            } catch (error) {
                this.clients = [];
            }
        },

        openItem(item) {
            this.$router.push(`/chc/${item.id}`);
        },

        async openNext() {
            this.openingNext = true;
            try {
                const result = await axios.get('/api/admin/user-events/credit-certificates/next');
                if (result.data.content.id) this.$router.push(`/chc/${result.data.content.id}`);
                else this.$dialog.notify.info('Nenhuma CHC pendente na fila.', { position: 'top-right', timeout: 3000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.openingNext = false;
            }
        },

        // CSV com os filtros atuais (BOM para o Excel abrir com acentos).
        async exportCsv() {
            this.exporting = true;
            try {
                const result = await axios.get('/api/admin/user-events/credit-certificates', {
                    params: { page: 1, limit: 200, search: this.searchParams() }
                });
                const rows = result.data.content.data;
                const header = ["Protocolo", "Enviada em", "Caso", "Requerente", "Tipo", "CPF/CNPJ do requerente", "Processo", "Ramo", "Credores", "Total (R$)", "Status"];
                const cell = (value) => `"${String(value === null || value === undefined ? "" : value).replace(/"/g, '""')}"`;
                const lines = rows.map((row) => [row.protocol, this.formatDateTime(row.created_at), row.client_name, row.requester_name, nameOf(requesterTypes, row.requester_type),
                    row.requester_registration, row.process_number, row.justice_branch ? nameOf(justiceBranches, row.justice_branch) : "", row.creditors_count,
                    this.formatMoney(row.total_brl), nameOf(statuses, row.status)].map(cell).join(";"));
                const blob = new Blob(["﻿" + [header.map(cell).join(";"), ...lines].join("\r\n")], { type: "text/csv;charset=utf-8" });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `chc-${this.$moment().format("YYYYMMDD-HHmm")}.csv`;
                link.click();
                URL.revokeObjectURL(link.href);
                if (result.data.content.paging.total > rows.length) {
                    this.$dialog.notify.info(`Exportadas as ${rows.length} primeiras de ${result.data.content.paging.total}. Use os filtros para exportar o restante.`, { position: 'top-right', timeout: 6000 });
                }
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.exporting = false;
            }
        },
    },
};
</script>

<style scoped>
.chc-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin-bottom: 16px; }
.chc-card { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 12px 16px; border: 1px solid #e4e7ec; border-radius: 10px; background: #fff; text-align: left; cursor: pointer; transition: border-color 0.15s, box-shadow 0.15s; }
.chc-card:hover { border-color: #98a2b3; }
.chc-card--active { border-color: #27306b; box-shadow: 0 0 0 1px #27306b inset; }
.chc-card__label { font-size: 12.5px; color: #475467; }
.chc-card__value { font-size: 20px; font-weight: 700; color: #101828; }
.chc-card__value--warning { color: #d97706; }
.chc-card__value--info { color: #2563eb; }
.chc-card__value--purple { color: #7c3aed; }
.chc-card__value--teal { color: #0d9488; }
.chc-card__value--success { color: #16a34a; }
.chc-card__value--danger { color: #dc2626; }
.chc-nowrap { white-space: nowrap; }
</style>
